/*
 * FiltroMenu.js — Pastilla de filtro con menú de casillas (C-025, ver diseno/controles.md).
 *
 * "Contador: Todos ▾" · "Contador: Ángel" · "Contador: Mis clientes +1". El nombre del filtro no
 * desaparece al elegir. Se pueden elegir varios. Activo, la pastilla queda prendida y lleva su ×
 * como botón aparte; "Todos", al pie del menú, también lo quita.
 * Teclado: Enter o espacio abren; el foco va a la primera casilla; Esc cierra y regresa a la pastilla.
 *
 * props:
 *   etiqueta    'Contador'
 *   textoTodos  'Todos' | 'Todas'
 *   opciones    [{ valor, texto, corto?, detalle }]; `destacada` (ej. "Mis clientes") va arriba, separada
 *   destacada   { valor, texto, detalle } | null
 *   seleccion   [valor]
 *   onCambiar   (nuevaSeleccion) => {}
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { BotonIcono } from './Tooltip';

export default function FiltroMenu({ etiqueta, textoTodos = 'Todos', opciones, destacada = null, seleccion, onCambiar }) {
  const [abierto, setAbierto] = useState(false);
  const caja = useRef(null);
  const pastilla = useRef(null);
  const todas = destacada ? [destacada, ...opciones] : opciones;
  // En la pastilla va el texto corto si lo hay ("Contador: Yohali"); en el menú, el completo.
  const textoDe = (valor) => { const o = todas.find(x => x.valor === valor) || { texto: valor }; return o.corto || o.texto; };
  const activo = seleccion.length > 0;
  const valor = !activo ? textoTodos
    : seleccion.length === 1 ? textoDe(seleccion[0])
    : `${textoDe(seleccion[0])} +${seleccion.length - 1}`;

  useEffect(() => {
    if (!abierto) return undefined;
    const primera = caja.current && caja.current.querySelector('input[type="checkbox"]');
    if (primera) primera.focus();
    const fuera = (e) => { if (caja.current && !caja.current.contains(e.target)) setAbierto(false); };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto]);

  const cerrar = () => {
    setAbierto(false);
    if (pastilla.current) pastilla.current.focus();
  };
  const alternar = (v) => onCambiar(seleccion.includes(v) ? seleccion.filter(x => x !== v) : [...seleccion, v]);

  const opcion = (o) => (
    <label key={o.valor} className="filtro-menu-opcion">
      <input type="checkbox" checked={seleccion.includes(o.valor)} onChange={() => alternar(o.valor)} />
      <span className="filtro-menu-texto">{o.texto}</span>
      {o.detalle && <span className="filtro-menu-detalle">{o.detalle}</span>}
    </label>
  );

  return (
    <span
      className="filtro-menu"
      ref={caja}
      onKeyDown={e => { if (e.key === 'Escape' && abierto) { e.stopPropagation(); cerrar(); } }}
    >
      <button
        ref={pastilla}
        type="button"
        className={`pastilla${activo ? ' prendida' : ''}`}
        aria-haspopup="true"
        aria-expanded={abierto}
        onClick={() => setAbierto(a => !a)}
      >
        {etiqueta}: <b>{valor}</b>
        <ChevronDown size={14} aria-hidden="true" />
      </button>
      {activo && (
        <BotonIcono className="pastilla-quitar" etiqueta={`Quitar filtro ${etiqueta}`} onClick={() => onCambiar([])}>
          <X size={13} aria-hidden="true" />
        </BotonIcono>
      )}
      {abierto && (
        <div className="filtro-menu-panel" role="group" aria-label={etiqueta}>
          {destacada && <>{opcion(destacada)}<hr /></>}
          {opciones.map(opcion)}
          <hr />
          <button type="button" className="filtro-menu-todos" onClick={() => { onCambiar([]); cerrar(); }}>
            {textoTodos}
          </button>
        </div>
      )}
    </span>
  );
}
